import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React, { useEffect, useState } from 'react'

import { BeltVisual } from '@goodfellas/belt-visual'
import * as Belt from '@goodfellas/graduation'

import { goodfellasApi } from '../../apis'
import { normalizeBeltColor } from '../../functions/graduation'

// Define types locally or import from a shared location if available
interface SearchResult {
  id: string
  type: 'FIGHTER' | 'GYM'
  name: string
  avatar: string | null
  belt?: Belt.Colors
  degree?: number
  gym?: string // Name of the gym for fighters
  location?: string // Address for gyms
  size?: number // Member count for gyms
}

export default function ProfileSearch() {
  const { t } = useTranslation('common')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [showResults, setShowResults] = useState(false)
  const [loading, setLoading] = useState(false)

  // Debounce search
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (query.length >= 2) {
        setLoading(true)
        try {
          const response = await goodfellasApi.get(
            `/public/profiles/search?q=${encodeURIComponent(query)}`,
          )
          if (response.data.success) {
            setResults(response.data.data)
            setShowResults(true)
          } else {
            setResults([])
          }
        } catch {
          setResults([])
        } finally {
          setLoading(false)
        }
      } else {
        setResults([])
        setShowResults(false)
      }
    }, 400) // 400ms delay

    return () => clearTimeout(delayDebounceFn)
  }, [query])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
  }

  // Helper to render detail (belt or size)
  const renderDetail = (result: SearchResult) => {
    if (result.type === 'FIGHTER') {
      if (result.belt) {
        const beltVal = normalizeBeltColor(result.belt)
        if (beltVal) {
          return t(`belts.${beltVal.toLowerCase()}`)
        }
      }
      return 'Atleta' // Fallback label for fighters without belt
    }

    // It's a GYM
    return result.size
      ? `${result.size} ${t('search.members')}`
      : t('search.gym')
  }

  // Helper to render sub-detail (gym name for FIGHTER, location for GYM)
  const renderSubDetail = (result: SearchResult) => {
    if (result.type === 'FIGHTER') {
      return result.gym
    }
    return result.location
  }

  return (
    <div className="w-full relative z-50 px-4 md:px-0 max-w-2xl mx-auto">
      {/* Search Input Container */}
      <div className="relative group">
        <div className="absolute inset-0 bg-red-600 blur opacity-20 group-hover:opacity-30 transition-opacity rounded-xl"></div>
        <div className="relative flex items-center bg-gray-800 border border-white/10 rounded-xl px-4 py-3 shadow-2xl backdrop-blur-sm">
          <input
            type="text"
            className="w-full bg-transparent text-white text-lg placeholder-white/40 focus:outline-none font-sans"
            placeholder={t('search.placeholder') as string}
            value={query}
            onChange={handleSearch}
          />
          {loading ? (
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          )}
        </div>
      </div>

      {/* Results Dropdown */}
      {showResults && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 mx-4 md:mx-0 bg-gray-800 border border-white/10 rounded-xl overflow-hidden shadow-2xl backdrop-blur-md max-h-[60vh] overflow-y-auto z-500">
          {results.map((result) => (
            <Link
              key={result.id}
              href={
                result.type === 'GYM'
                  ? `/gyms/${result.id}`
                  : `/fighters/${result.id}`
              }
              className="flex items-center p-4 hover:bg-white/5 cursor-pointer transition-colors border-b border-white/5 last:border-0 group/item"
            >
              {/* Avatar / Icon */}
              <div className="flex-shrink-0 mr-4">
                <div className="relative w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center border border-white/10 overflow-hidden">
                  {result.avatar ? (
                    <img
                      src={result.avatar}
                      alt={result.name}
                      className="object-cover w-full h-full block"
                      style={{ objectFit: 'cover' }}
                    />
                  ) : result.type === 'FIGHTER' ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-white/60"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-white/60"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  )}
                </div>
              </div>

              {/* Info */}
              <div className="flex-grow">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-white font-bold text-lg font-serif truncate">
                    {result.name}
                  </h3>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-[10px] md:text-xs font-bold px-2 py-0.5 rounded uppercase tracking-wider border-2 border-white/20 bg-white/10 text-white/80 whitespace-nowrap">
                      {renderDetail(result)}
                    </span>
                    {result.type === 'FIGHTER' && result.belt && (
                      <div
                        title={
                          t(
                            `belts.${normalizeBeltColor(result.belt)}`,
                          ) as string
                        }
                        className="hidden md:block"
                      >
                        <BeltVisual
                          color={
                            normalizeBeltColor(result.belt) || Belt.Colors.White
                          }
                          size="sm"
                        />
                      </div>
                    )}
                  </div>
                </div>
                {renderSubDetail(result) && (
                  <p className="text-white/40 text-sm font-sans text-left">
                    {renderSubDetail(result)}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
